import React from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ShieldExclamationIcon, ArrowPathIcon, HomeIcon } from '@heroicons/react/24/outline';
import apiClient from '../../services/apiClient';

const SharePointAccessDenied: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const siteUrl = searchParams.get('siteUrl');
  const customerId = searchParams.get('customerId');
  const phaseId = searchParams.get('phaseId');

  const handleReconnect = () => {
    if (customerId && phaseId) {
      // Redirect to OAuth start endpoint
      const oauthUrl = apiClient.startPhaseSharePointOAuth(customerId, phaseId, siteUrl || undefined);
      window.location.href = oauthUrl;
    } else {
      // Fallback if we don't have enough info
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-10 rounded-2xl shadow-soft-xl border border-gray-100">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-red-100 mb-6">
            <ShieldExclamationIcon className="h-12 w-12 text-brand-red" aria-hidden="true" />
          </div>
          <h2 className="text-3xl font-extrabold text-brand-navy mb-2">
            Access Denied
          </h2>
          <p className="text-lg font-medium text-gray-600 mb-6">
            SharePoint Connection Issue
          </p>
        </div>

        <div className="space-y-6">
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
              What happened?
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              We were able to connect to SharePoint, but your account doesn't have the necessary permissions to access this site.
            </p>
          </div>

          {siteUrl && (
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
              <h3 className="text-sm font-semibold text-blue-900 uppercase tracking-wider mb-3">
                Target Site
              </h3>
              <p className="text-sm text-blue-800 break-all font-mono">
                {siteUrl}
              </p>
            </div>
          )}

          <div className="bg-amber-50 rounded-xl p-6 border border-amber-100">
            <h3 className="text-sm font-semibold text-amber-900 uppercase tracking-wider mb-3">
              How to fix this?
            </h3>
            <ul className="text-sm text-amber-800 space-y-2 list-disc pl-5">
              <li>Ask the <strong>SharePoint Site Owner</strong> to add you to the "Site Members" group.</li>
              <li>Ensure you have "Edit" or "Contribute" permissions.</li>
              <li>Try reconnecting your account if you recently changed permissions.</li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col gap-3 mt-8">
          <button
            onClick={handleReconnect}
            className="btn-primary w-full py-3 text-base flex items-center justify-center gap-2"
          >
            <ArrowPathIcon className="h-5 w-5" />
            Reconnect SharePoint
          </button>

          <button
            onClick={() => navigate('/dashboard')}
            className="btn-secondary w-full py-3 text-base flex items-center justify-center gap-2"
          >
            <HomeIcon className="h-5 w-5" />
            Back to Dashboard
          </button>
        </div>

        <div className="text-center pt-4">
          <p className="text-xs text-gray-400">
            If you believe this is an error, please contact your administrator.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SharePointAccessDenied;
